import { Constructor, Unboxed } from '@skylab-shared/types';
import { serializeParams, Singleton } from '@skylab-shared/utils';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { plainToClassFromExist, plainToInstance } from 'class-transformer';
import { handle401StatusRequestInterceptor } from './interceptors/401-status-request-interceptor';
import { handle403StatusRequestInterceptor } from './interceptors/403-status-request-interceptor';
import { handle404StatusRequestInterceptor } from './interceptors/404-status-request-interceptor';
import { handle5xxStatusRequestInterceptor } from './interceptors/5xx-status-request-interceptor';
import { httpRequestInterceptor } from './interceptors/http-request-interceptor';
import { markRequestRequestInterceptor } from './interceptors/mark-request-request-interceptor';
import { tagUrl } from './interceptors/tagged-url-request-interceptor';
import { handleTimeoutRequestInterceptor } from './interceptors/timeout-request-interceptor';
import { handleDateInRequest } from './transformers/date-to-utc-date-time-string-request-handler';

export interface InternalHttpAxiosRequestConfig<TGetDTO> extends AxiosRequestConfig {
    dto?: Constructor<Unboxed<TGetDTO>>;
    dtoObject?: TGetDTO;
}

export interface InterceptorRequestConfig extends InternalAxiosRequestConfig {
    taggedUrl: any;
}
export interface IResponseError extends AxiosError {
    config: InterceptorRequestConfig;
}

export type IResponse = AxiosResponse & IResponseError;

export interface Interceptor {
    request?(config: InterceptorRequestConfig): void;
    requestError?(rejection: IResponse): void;
    response?(response: AxiosResponse): void;
    responseError?(rejection: IResponse): void;
}

export enum InterceptorStage {
    Pre = 1,
    Business,
    Core,
    Clean
}

export enum InterceptorType {
    Request = 1,
    Response
}

@Singleton
export class HttpService {
    private http: AxiosInstance;
    private interceptorsCollection: { interceptor: Interceptor, stage: InterceptorStage }[] = [];
    private interceptorsExecutionOrder: InterceptorStage[];

    public constructor() {
        const config: AxiosRequestConfig = {
            timeout: 1000 * 30,
            withCredentials: true,
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'Content-Type': 'application/json;charset=UTF-8'
            },
            transformResponse: [
                d => {
                    try {
                        return d instanceof Blob ? d : JSON.parse(d);
                    } catch (err) {
                        return null;
                    }
                }
            ],
            transformRequest: [
                d => JSON.stringify(d),
                handleDateInRequest
            ],
            paramsSerializer: params => serializeParams(params)
        };
        this.interceptorsExecutionOrder = [
            InterceptorStage.Pre,
            InterceptorStage.Business,
            InterceptorStage.Core,
            InterceptorStage.Clean
        ];
        this.http = axios.create(config);
        this.setInterceptors();
    }

    private registerInterceptors(): void {
        this.insertInterceptorInProperOrder(tagUrl, InterceptorStage.Pre);
        this.insertInterceptorInProperOrder(handle5xxStatusRequestInterceptor, InterceptorStage.Business);
        this.insertInterceptorInProperOrder(handle401StatusRequestInterceptor, InterceptorStage.Business);
        this.insertInterceptorInProperOrder(handle403StatusRequestInterceptor, InterceptorStage.Business);
        this.insertInterceptorInProperOrder(markRequestRequestInterceptor, InterceptorStage.Pre);
        this.insertInterceptorInProperOrder(httpRequestInterceptor, InterceptorStage.Business);
        this.insertInterceptorInProperOrder(handle404StatusRequestInterceptor, InterceptorStage.Business);
        this.insertInterceptorInProperOrder(handleTimeoutRequestInterceptor, InterceptorStage.Business);
    }

    private insertInterceptorInProperOrder(interceptor: Function, stage: InterceptorStage): void {
        const index = this.lastIndexOf(stage);
        this.interceptorsCollection.splice(index, 0, { interceptor: interceptor(), stage: stage });
    }

    private lastIndexOf(stage: InterceptorStage): number {
        let index = -1;
        const execOrder = this.interceptorsExecutionOrder.indexOf(stage);
        for (let i = this.interceptorsCollection.length - 1; i >= 0; --i) {
            const currInterceptor = this.interceptorsCollection[i];
            const currInterceptorExecOrderIndex = this.interceptorsExecutionOrder.indexOf(currInterceptor.stage);
            if (currInterceptorExecOrderIndex === -1)
                continue;
            if (execOrder >= currInterceptorExecOrderIndex) {
                index = this.interceptorsCollection.indexOf(currInterceptor) + 1;
                break;
            }
        }
        return index < 0 ? 0 : index;
    }

    private interceptRequest(config: InterceptorRequestConfig): InterceptorRequestConfig {
        this.runInterceptors('request', config);
        return config;
    }

    private interceptResponse(response: AxiosResponse): AxiosResponse {
        this.runInterceptors('response', response);
        return response;
    }

    private interceptRequestError(error: IResponse): Promise<never> {
        this.runInterceptors('requestError', error);
        return Promise.reject(error.response);
    }

    private interceptResponseError(error: IResponse): Promise<never> {
        this.runInterceptors('responseError', error);
        return Promise.reject(error.response);
    }

    private runInterceptors(type: keyof Interceptor, data: any): void {
        this.interceptorsCollection
            .forEach(elem => {
                if (typeof elem.interceptor[type] === 'function') {
                    elem.interceptor[type](data);
                }
            });
    }

    private setInterceptors(): void {
        this.registerInterceptors();
        this.http.interceptors.request.use(this.interceptRequest.bind(this) as any, this.interceptRequestError.bind(this) as any);
        this.http.interceptors.response.use(this.interceptResponse.bind(this), this.interceptResponseError.bind(this));
    }

    private prepareDTOConfig<T = any>(config?: InternalHttpAxiosRequestConfig<T>): InternalHttpAxiosRequestConfig<T> | undefined {
        let dtoConfig: InternalHttpAxiosRequestConfig<T> | undefined;

        if (config?.dto) {
            dtoConfig = { ...config, transformResponse: (res => plainToInstance(config.dto!, JSON.parse(res))) };
        } else if (config?.dtoObject) {
            dtoConfig = { ...config, transformResponse: (res => plainToClassFromExist(config.dtoObject!, JSON.parse(res))) };
        }

        return dtoConfig || config;
    }

    public get<T = any, R = AxiosResponse<T>>(url: string, config?: InternalHttpAxiosRequestConfig<T>): Promise<R> {
        return this.http.get(url, this.prepareDTOConfig(config));
    }

    public post<T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: InternalHttpAxiosRequestConfig<T>): Promise<R> {
        return this.http.post(url, data, this.prepareDTOConfig(config));
    }

    public put<T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: AxiosRequestConfig): Promise<R> {
        return this.http.put(url, data, config);
    }

    public patch<T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: AxiosRequestConfig): Promise<R> {
        return this.http.patch(url, data, config);
    }

    public delete<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
        return this.http.delete(url, config);
    }

    public sendRequest<T = any>(config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.http(config);
    }
}

export const HttpServiceBase = new HttpService();
